import { BALANCE, type AccountSummary, type PlayerProfile } from '@2k48/shared';
import { api, setApiAuthToken } from '../../net/client';
import { storage } from '../../platform/storage';

/**
 * The account layer on top of the guest identity.
 *
 * Guest-first is preserved exactly: nothing here runs before the app is
 * playable, and an account is something a player *adds* to the identity they
 * already have. Registration therefore sends the device id and gets the same
 * `playerId` back — history, Elo and achievements never move.
 *
 * The token is an opaque server secret kept in `platform/storage`, which is
 * Capacitor Preferences on device (SharedPreferences / UserDefaults) and
 * localStorage on the web. That choice follows from the bundle shipping to
 * three origins: a cookie that must be `SameSite=None; Secure` for the web view
 * is not sent at all from `file://`, while one header behaves the same
 * everywhere.
 */

const TOKEN_KEY = '2k48.auth.token';
const ISSUED_KEY = '2k48.auth.issuedAt';

/** Same-origin nudge so a second tab learns about a sign-in or a sign-out. */
const CHANNEL = '2k48.auth';

interface AuthPayload {
  account: AccountSummary;
  profile: PlayerProfile | null;
  session: { token: string; expiresAt: number; rememberMe: boolean };
}

export interface RegisterInput {
  deviceId: string;
  username: string;
  email: string;
  password: string;
  avatarId?: number;
  rememberMe?: boolean;
}

class Auth {
  account = $state<AccountSummary | null>(null);
  /** When the current token stops working, or null while signed out. */
  expiresAt = $state<number | null>(null);
  /** True only between "we found a token" and "we know whether it still works". */
  restoring = $state(false);

  private token: string | null = null;
  private issuedAt = 0;
  private channel: BroadcastChannel | null = null;
  /** Set while a rotation is in flight, so concurrent callers await one request. */
  private rotation: Promise<boolean> | null = null;

  get signedIn(): boolean {
    return this.account !== null;
  }

  /**
   * Is there a token on this device at all? Storage only — it says nothing
   * about whether the server still honours it.
   *
   * The distinction matters exactly once, and it matters a lot: `restore()`
   * answers null for "revoked" and for "offline" alike, deliberately, because
   * the app does the same thing either way. The one caller that cannot treat
   * them alike is the boot path, which has to decide whether a cached identity
   * is stale or merely unreachable. A token still being here means the second.
   */
  async hasStoredToken(): Promise<boolean> {
    return (await storage.get(TOKEN_KEY)) !== null;
  }

  /**
   * Read the stored token and find out who it belongs to.
   *
   * Returns the account's player profile when the session is alive, so the
   * caller can adopt that identity instead of the device's guest. Returns null
   * for every other case — no token, expired, revoked, server unreachable —
   * because all of them mean the same thing to the app: keep playing as a
   * guest.
   */
  async restore(): Promise<PlayerProfile | null> {
    this.listen();

    const token = await storage.get(TOKEN_KEY);
    if (!token) return null;

    this.restoring = true;
    this.adoptToken(token, Number((await storage.get(ISSUED_KEY)) ?? 0));

    try {
      // Rotate rather than merely verify when the token has been around a
      // while: a session that is only ever read expires on schedule no matter
      // how actively it is used, and the player is signed out mid-session for
      // no reason they can perceive.
      if (this.shouldRotate()) {
        const rotated = await this.refresh();
        if (rotated) return this.profileOf(this.account);
      }

      const me = await api<{ account: AccountSummary; profile: PlayerProfile | null }>('/auth/me');
      this.account = me.account;
      return me.profile;
    } catch {
      // Distinguishing "revoked" from "offline" here would need the status, and
      // the useful behaviour is identical: fall back to the guest. What must
      // not happen is clearing the stored token on a network blip, which would
      // sign a player out for walking into a lift.
      this.account = null;
      return null;
    } finally {
      this.restoring = false;
    }
  }

  /** Convert this device's guest into an account. Same player, now with a password. */
  async register(input: RegisterInput): Promise<PlayerProfile | null> {
    const payload = await api<AuthPayload>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return this.accept(payload);
  }

  async login(
    identifier: string,
    password: string,
    rememberMe: boolean,
  ): Promise<PlayerProfile | null> {
    const payload = await api<AuthPayload>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ identifier, password, rememberMe }),
    });
    return this.accept(payload);
  }

  /**
   * Rotate the token. Safe to call at any time; concurrent callers share one
   * request rather than racing each other into invalidating a fresh token.
   */
  async refresh(): Promise<boolean> {
    this.rotation ??= this.rotate().finally(() => {
      this.rotation = null;
    });
    return this.rotation;
  }

  /**
   * Sign out. The server call is best-effort and the local state is cleared
   * either way: a player who taps "cerrar sesión" on a train must end up signed
   * out on this device even though the revocation could not be delivered.
   */
  async logout(): Promise<void> {
    try {
      if (this.token) await api<void>('/auth/logout', { method: 'POST' });
    } catch {
      /* the local half is what the player asked for */
    }
    await this.clear();
    this.channel?.postMessage('changed');
  }

  // --- internals -------------------------------------------------------------

  private async rotate(): Promise<boolean> {
    if (!this.token) return false;

    try {
      const payload = await api<AuthPayload>('/auth/refresh', { method: 'POST' });
      await this.accept(payload, { broadcast: false });
      return true;
    } catch {
      // A 401 here has an innocent explanation worth handling: another tab
      // rotated first, and its replacement is already in storage. Adopt it
      // before concluding the session is gone.
      const stored = await storage.get(TOKEN_KEY);
      if (stored && stored !== this.token) {
        this.adoptToken(stored, Number((await storage.get(ISSUED_KEY)) ?? 0));
        return true;
      }
      return false;
    }
  }

  private async accept(
    payload: AuthPayload,
    options: { broadcast?: boolean } = {},
  ): Promise<PlayerProfile | null> {
    this.account = payload.account;
    this.adoptToken(payload.session.token, Date.now(), payload.session.expiresAt);

    await storage.set(TOKEN_KEY, payload.session.token);
    await storage.set(ISSUED_KEY, String(this.issuedAt));

    if (options.broadcast !== false) this.channel?.postMessage('changed');
    return payload.profile;
  }

  private adoptToken(token: string, issuedAt: number, expiresAt?: number): void {
    this.token = token;
    this.issuedAt = Number.isFinite(issuedAt) ? issuedAt : Date.now();
    if (expiresAt !== undefined) this.expiresAt = expiresAt;
    setApiAuthToken(token);
  }

  private async clear(): Promise<void> {
    this.token = null;
    this.issuedAt = 0;
    this.account = null;
    this.expiresAt = null;
    setApiAuthToken(null);
    await storage.remove(TOKEN_KEY);
    await storage.remove(ISSUED_KEY);
  }

  private shouldRotate(): boolean {
    return Date.now() - this.issuedAt >= BALANCE.account.sessionRefreshAfterMs;
  }

  private async profileOf(account: AccountSummary | null): Promise<PlayerProfile | null> {
    if (!account) return null;
    try {
      const me = await api<{ profile: PlayerProfile | null }>('/auth/me');
      return me.profile;
    } catch {
      return null;
    }
  }

  /**
   * Cross-tab coordination.
   *
   * Only a bare "something changed" is sent, never the token: every tab shares
   * the same storage, so re-reading it is both sufficient and one less place
   * for a secret to travel through. Signing out in one tab must take effect in
   * the others — otherwise the second tab keeps a working session that its
   * owner believes they closed.
   */
  private listen(): void {
    if (this.channel || typeof BroadcastChannel === 'undefined') return;

    this.channel = new BroadcastChannel(CHANNEL);
    this.channel.onmessage = () => {
      void storage.get(TOKEN_KEY).then(async (token) => {
        if (!token) {
          if (this.signedIn) {
            await this.clear();
            // A view showing account-only content would now be showing it to a
            // guest. Reloading is blunt, and it is also the only way to be sure
            // no in-flight screen keeps rendering the signed-out account.
            window.location.reload();
          }
          return;
        }
        if (token === this.token) return;
        this.adoptToken(token, Date.now());
        window.location.reload();
      });
    };
  }
}

export const auth = new Auth();
